import { Tables } from "@services/storage/model";
import { CacheTypes } from "./interfaces";
// CacheUtils class definition
// it will hold object with cache data, indexed by key
// and will provide methods to get, set and remove data
// It will implement StorageUtils interface
// It will implement IndexedDB interface
// It will implement Cache class

export type CacheTables =
  | "ActiveGroups"
  | "AddressBlacklist"
  | "Agents"
  | "AppointmentTimeTypes"
  | "BoilerManufacturers"
  | "BookingRestrictions"
  | "BuildingTypes"
  | "CallTypes"
  | "Discounts"
  | "DiscountTypes"
  | "FailedReasons"
  | "GlobalSettings"
  | "GroupTypes"
  | "HeatingTypes"
  | "JobScripts"
  | "JobTypes"
  | "JobTypesOptions"
  | "OfferAllocationReasons"
  | "OtherPhoneCalls"
  | "PaymentFrequencies"
  | "PaymentTypes"
  | "PostcodeAreas"
  | "ResponseTimes"
  | "ServiceAgreementDeclinedReason"
  | "ServiceAgreementTypes"
  | "ServiceAgreementBillingFrequencies"
  | "ServiceAgreementDurations"
  | "ServiceAgreementExcesses"
  | "TimeRequirementsTypes"
  | "TradeGroupsMap"
  | "TradeTypes"
  | "VatRates";

type CacheUtilsTypes =
  | CacheTypes.ActiveGroups
  | CacheTypes.AddressBlacklist
  | CacheTypes.Agents
  | CacheTypes.AppointmentTimeTypes
  | CacheTypes.BoilerManufacturers
  | CacheTypes.BookingRestrictions
  | CacheTypes.BuildingTypes
  | CacheTypes.CallTypes
  | CacheTypes.Discounts
  | CacheTypes.DiscountTypes
  | CacheTypes.FailedReasons
  | CacheTypes.GlobalSettings
  | CacheTypes.GroupTypes
  | CacheTypes.HeatingTypes
  | CacheTypes.JobScripts
  | CacheTypes.JobTypes
  | CacheTypes.JobTypeOptions
  | CacheTypes.OfferAllocationReasons
  | CacheTypes.OtherPhoneCalls
  | CacheTypes.PaymentFrequencies
  | CacheTypes.PaymentTypes
  | CacheTypes.PostcodeAreas
  | CacheTypes.ResponseTimes
  | CacheTypes.ServiceAgreementDeclinedReason
  | CacheTypes.ServiceAgreementTypes
  | CacheTypes.ServiceAgreementBillingFrequency
  | CacheTypes.ServiceAgreementDuration
  | CacheTypes.ServiceAgreementExcess
  | CacheTypes.TimeRequirementsTypes
  | CacheTypes.TradeGroupsMap
  | CacheTypes.TradeTypes
  | CacheTypes.VATRates;

export default class CacheUtils {
  private cacheStore: { [key in CacheTables]: Cache<CacheUtilsTypes> } = {
    TradeTypes: new Cache<CacheTypes.TradeTypes>("TradeTypes"),
    JobTypes: new Cache("JobTypes"),
    ActiveGroups: new Cache("ActiveGroups"),
    Agents: new Cache("Agents"),
    AddressBlacklist: new Cache("AddressBlacklist"),
    AppointmentTimeTypes: new Cache("AppointmentTimeTypes"),
    BookingRestrictions: new Cache("BookingRestrictions"),
    BoilerManufacturers: new Cache("BoilerManufacturers"),
    BuildingTypes: new Cache("BuildingTypes"),
    CallTypes: new Cache("CallTypes"),
    Discounts: new Cache("Discounts"),
    DiscountTypes: new Cache("DiscountTypes"),
    FailedReasons: new Cache("FailedReasons"),
    GlobalSettings: new Cache("GlobalSettings"),
    GroupTypes: new Cache("GroupTypes"),
    HeatingTypes: new Cache("HeatingTypes"),
    JobScripts: new Cache("JobScripts"),
    JobTypesOptions: new Cache("JobTypesOptions"),
    OfferAllocationReasons: new Cache("OfferAllocationReasons"),
    OtherPhoneCalls: new Cache("OtherPhoneCalls"),
    PaymentFrequencies: new Cache("PaymentFrequencies"),
    PaymentTypes: new Cache("PaymentTypes"),
    PostcodeAreas: new Cache("PostcodeAreas"),
    ResponseTimes: new Cache("ResponseTimes"),
    ServiceAgreementDeclinedReason: new Cache("ServiceAgreementDeclinedReason"),
    ServiceAgreementTypes: new Cache("ServiceAgreementTypes"),
    ServiceAgreementBillingFrequencies: new Cache("ServiceAgreementBillingFrequencies"),
    ServiceAgreementDurations: new Cache("ServiceAgreementDurations"),
    ServiceAgreementExcesses: new Cache("ServiceAgreementExcesses"),
    TimeRequirementsTypes: new Cache("TimeRequirementsTypes"),
    TradeGroupsMap: new Cache("TradeGroupsMap"),
    //   TradeGroups: new Cache("TradeGroups"),
    VatRates: new Cache("VatRates"),
  };

  public getEmptyIndexes = (): Cache<CacheUtilsTypes>[] => {
    const emptyIndexes: Cache<CacheUtilsTypes>[] = [];
    Object.values(this.cacheStore).forEach((cache) => {
      if (cache.getCacheSize() === 0) {
        emptyIndexes.push(cache);
      }
    });
    return emptyIndexes;
  };

  public areEmptyStoresPresent(): boolean {
    return this.getAllLengths().some((size) => size === 0);
  }

  public getAllLengths = () => {
    return Object.values(this.cacheStore).map((cache) => cache.getCacheSize());
  };

  public getLength(table: CacheTables): Number {
    return this.cacheStore[table].getCacheSize();
  }

  public isInCacheStore(key: CacheTables): boolean {
    return this.cacheStore[key] !== undefined;
  }

  public getValue(key: CacheTables): Cache<CacheUtilsTypes> {
    return this.cacheStore[key];
  }

  public setValue(key: CacheTables, value: Cache<CacheUtilsTypes>): void {
    this.cacheStore[key] = value;
  }

  public setCache(key: CacheTables, value: CacheUtilsTypes[]): void {
    this.cacheStore[key].setCache(value);
  }

  public getCache<T>(key: CacheTables): Array<T> {
    return this.cacheStore[key].getCache() as Array<T>;
  }
}

// Cache class definition
// it will hold data obtained from api method calls
// it will refresh on set interval

class Cache<T> {
  private cache: Array<T> = [];
  private cacheSize: number = 0;
  private tableName: Tables;

  constructor(tableName: Tables) {
    // log with logger
    // logger.debug(nameSpace, `Cache constructor called for ${tableName}`);
    this.tableName = tableName;
  }

  public getCacheSize(): number {
    return this.cacheSize;
  }

  public getCache() {
    return this.cache;
  }

  public setCache(cache: Array<T>) {
    // log with logger
    this.cache = cache;
  }
}

// cache helpers class
export class CacheHelpers {
  public static dateDifference(current: number, future: number): string {
    let diffTime = Math.abs(future - current);
    let days = diffTime / (24 * 60 * 60 * 1000);
    let hours = (days % 1) * 24;
    let minutes = (hours % 1) * 60;
    let secs = (minutes % 1) * 60;
    [days, hours, minutes, secs] = [Math.floor(days), Math.floor(hours), Math.floor(minutes), Math.floor(secs)];

    return `${days} days, ${hours} hours, ${minutes} minutes, ${secs} seconds`;
  }
}
